// Stage / step constants aligned with the deployed Cabinet Depot backend.
// Items have a `current_step` string and a `progress` array of { step, completed, count, total, last_user_name, last_timestamp }.
// Pipeline: New -> Building -> QA -> Shipped.

export const STEPS = ["New", "Building", "QA", "Shipped"];

export const STEP_COLORS = {
  New:      { bg: "#E4E4E7", text: "#3F3F46", border: "#A1A1AA" },
  Building: { bg: "#DBEAFE", text: "#1D4ED8", border: "#3B82F6" },
  QA:       { bg: "#FFEDD5", text: "#C2410C", border: "#EA580C" },
  Shipped:  { bg: "#D1FAE5", text: "#047857", border: "#10B981" },
  Cancelled:{ bg: "#FEE2E2", text: "#991B1B", border: "#EF4444" },
};

export const STEP_LABEL = {
  New: "New",
  Building: "Building",
  QA: "QA",
  Shipped: "Shipped",
  Cancelled: "Cancelled",
};

export const ROLE_LABELS = {
  admin: "Admin",
  manager: "Manager",
  builder: "Builder",
};

export function itemProgressPct(item) {
  if (!item) return 0;
  if (item.cancelled) return 0;
  // progress array gives count/total per step (Building, QA, Shipped). Overall = completed-steps / total-steps.
  const steps = item.progress || [];
  if (!steps.length) return 0;
  const completed = steps.filter((s) => s.completed).length;
  return Math.round((completed / steps.length) * 100);
}

export function orderProgressPct(order) {
  if (!order) return 0;
  if (order.item_count == null) return 0;
  if (order.item_count === 0) return 0;
  return Math.round(((order.completed_count || 0) / order.item_count) * 100);
}

/**
 * An order is "completed" from the user's perspective if EITHER:
 *   - the backend has flipped status to "completed" (all items shipped), OR
 *   - the order has been signed (signed_at / signature_svg present).
 *
 * Cancelled orders are NEVER completed — they live in their own bucket and
 * must be filtered out of completed/active counts.
 *
 * The backend only sets status="completed" when every item reaches the
 * Shipped step. But in practice, signing the order is the operator's
 * "this job is done" gesture — the order should leave Active immediately
 * after signing, even if a few items remain at QA on the backend.
 */
export function isOrderCompleted(order) {
  if (!order) return false;
  if (order.status === "cancelled") return false; // cancelled is its own bucket
  if (order.status === "completed") return true;
  if (order.signed_at) return true;
  if (order.signature_svg) return true;
  // All items shipped (some backends only flip status on a later cron tick;
  // we want the UI to be honest about completion the instant counts agree).
  const itemCount = Number(order.item_count) || 0;
  const completedCount = Number(order.completed_count) || 0;
  if (itemCount > 0 && completedCount >= itemCount) return true;
  return false;
}

/** True when the order has been cancelled upstream (Base44). */
export function isOrderCancelled(order) {
  return order?.status === "cancelled";
}

/** Returns the status string we want to render in UI: "active" / "completed" / "cancelled". */
export function orderEffectiveStatus(order) {
  if (isOrderCancelled(order)) return "cancelled";
  return isOrderCompleted(order) ? "completed" : (order?.status || "active");
}

export function stepCountsForOrder(items) {
  const counts = { New: 0, Building: 0, QA: 0, Shipped: 0, Cancelled: 0 };
  items?.forEach((i) => {
    const key = i.cancelled ? "Cancelled" : (i.current_step || "New");
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}
